import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux'
import { Form, Button } from 'semantic-ui-react';
import { Formik, Field, Form as FormikForm } from 'formik';
import { FormikInputComponent } from '../../../utilities/formUtils';
import { getNotification, getParams, getVtParamMap } from '../data/selectors';
import { vtparammapSchema } from '../data/model';
import { createVtParamMap, editVtParamMap, setNotifyDone } from '../data/actions';
import { trim } from 'lodash';
import { getSegmapList, getCompanyList } from '../../common/data/selectors';
import { setAlertModalState } from '../../common/data/actions';
import CommonSelect from '../../common/components/CommonSelect';
import LedgerSearch from '../../ledger_search/components/LedgerSearch';
import axios from 'axios';
import { URL, getHeaderJWT } from '../../../store/path';

import userACL from '../../../store/access'
import Notification from '../../../utilities/notificationUtils';

const VtParamMapForm = (props) => {
    const dispatch = useDispatch()
    const vtParamMap = useSelector(state => getVtParamMap(state, props))
    const params = useSelector(state => getParams(state, props))
    const getAllSegmapList = useSelector((state) => getSegmapList(state, props))
    const companyList = useSelector((state) => getCompanyList(state, props))

    const [savedId, setSavedId] = useState(false)
    // ------------------ Local error state for per-row errors ------------------
    const [grpsErrors, setGrpsErrors] = React.useState({});
    // ------------------ GRPS: Add New Mapping dropdown state (persists across re-renders) ------------------
    const [grpsNewFrom, setGrpsNewFrom] = useState("");
    const [grpsNewTo, setGrpsNewTo] = useState("");
    // ------------------ VTS: Voucher Type Select ------------------
    const [vtOptions, setVtOptions] = useState([]);
    const [vtLoading, setVtLoading] = useState(false);

    const isEditMode = props.match.path === '/vtparam_map/edit/:id';

    // All fetched vt data (unfiltered)
    const [allVtData, setAllVtData] = useState([]);

    // Fetch voucher types when param_display_type is VTS
    useEffect(() => {
        if (trim(vtParamMap.param_display_type).toLowerCase() === "vts") {
            setVtLoading(true);
            axios.post(URL + '/master/rlb/vtdetails', {}, getHeaderJWT())
                .then(function (response) {
                    if (response.data && response.data.data) {
                        setAllVtData(response.data.data);
                    }
                    setVtLoading(false);
                })
                .catch(function () {
                    setVtLoading(false);
                });
        }
    }, [vtParamMap.param_display_type]);

    // Filter vtOptions by selected cid
    useEffect(() => {
        const filtered = vtParamMap.cid
            ? allVtData.filter(function (vt) { return vt.cid === vtParamMap.cid; })
            : allVtData;
        var options = filtered.map(function (vt) {
            return {
                key: vt.vtName,
                value: vt.vtName,
                text: vt.vtName,
            };
        });
        setVtOptions(options);
    }, [allVtData, vtParamMap.cid]);

    // Parse LCD PARAM_VALUE JSON to get ledger name (key of the JSON object)
    const parseLcdLedgerName = (paramValue) => {
        if (!paramValue) return '';
        try {
            const obj = JSON.parse(paramValue);
            const keys = Object.keys(obj || {});
            return keys.length > 0 ? keys[0] : '';
        } catch (e) {
            return '';
        }
    };

    // Compute initial values: pre-populate LCS/LNS/LCD search fields from PARAM_VALUE in edit mode
    const initialValues = {
        ...vtParamMap,
        lcs_ledger_name: isEditMode && trim(vtParamMap.param_display_type).toLowerCase() === 'lcs' ? vtParamMap.PARAM_VALUE : '',
        lns_ledger_search: isEditMode && trim(vtParamMap.param_display_type).toLowerCase() === 'lns' ? vtParamMap.PARAM_VALUE : '',
        lcd_ledger_search: isEditMode && trim(vtParamMap.param_display_type).toLowerCase() === 'lcd' ? parseLcdLedgerName(vtParamMap.PARAM_VALUE) : '',
    };

    // LCS handler: on ledger selection, set PARAM_VALUE to ledgerCode
    const handleLcsLedgerSelection = (e, values, setFieldValue, fieldName, obj, dispatch) => {
        if (obj) {
            setFieldValue(fieldName, obj.ledgerName);
            setFieldValue('PARAM_VALUE', obj.ledgerCode ? obj.ledgerCode : "");
        } else {
            setFieldValue(fieldName, '');
            setFieldValue('PARAM_VALUE', '');
        }
    };

    const handleLcsLedgerOnBlur = (e, values, setFieldValue, fieldName, obj, dispatch) => {
        if (!e.target.value) {
            setFieldValue(fieldName, '')
            setFieldValue('PARAM_VALUE', '')
        }
    }


    // LNS handler: on ledger selection, set PARAM_VALUE to ledgerName
    const handleLnsLedgerSelection = (e, values, setFieldValue, fieldName, obj, dispatch) => {
        if (obj) {
            setFieldValue(fieldName, obj.ledgerName);
            setFieldValue('PARAM_VALUE', obj.ledgerName ? obj.ledgerName : "");
        } else {
            setFieldValue(fieldName, '');
            setFieldValue('PARAM_VALUE', '');
        }
    };

    const handleLnsLedgerOnBlur = (e, values, setFieldValue, fieldName, obj, dispatch) => {
        if (!e.target.value) {
            setFieldValue(fieldName, '')
            setFieldValue('PARAM_VALUE', '')
        }
    }

    // LCD handler: on ledger selection, set PARAM_VALUE to JSON like {"Ledger Name":"Code"}
    const handleLcdLedgerSelection = (e, values, setFieldValue, fieldName, obj, dispatch) => {
        if (obj && obj.ledgerName) {
            setFieldValue(fieldName, obj.ledgerName);
            const paramObj = { [obj.ledgerName]: obj.ledgerCode ? obj.ledgerCode : "" };
            setFieldValue('PARAM_VALUE', JSON.stringify(paramObj));
        } else {
            setFieldValue(fieldName, '');
            setFieldValue('PARAM_VALUE', '');
        }
    };

    const handleLcdLedgerOnBlur = (e, values, setFieldValue, fieldName, obj, dispatch) => {
        if (!e.target.value) {
            setFieldValue(fieldName, '')
            setFieldValue('PARAM_VALUE', '')
        }
    }

    // PARAM_KEY is editable only for VP vendor ledger mappings:
    // TRANS_TYPE=VP + PARAM_GROUP=vendorLedgerCode + display type LCS, or
    // TRANS_TYPE=VP + PARAM_GROUP=vendorLedgerName + display type LNS
    const isParamKeyEditable = (values) => {
        const dt = trim(values.param_display_type).toLowerCase();
        const pg = trim(values.PARAM_GROUP).toLowerCase();
        const isVp = trim(values.TRANS_TYPE).toUpperCase() === 'VP';
        return isVp && ((pg === 'vendorledgercode' && dt === 'lcs') || (pg === 'vendorledgername' && dt === 'lns'));
    };

    const saveLedgermap = (values) => {
        // Remove LCS/LNS/LCD search display fields before submit
        delete values.lcs_ledger_name;
        delete values.lns_ledger_search;
        delete values.lcd_ledger_search;
        // Safety: strip any stray "undefined" key (could leak from buggy onBlur in older sessions)
        delete values.undefined;

        if (props.match.path === '/vtparam_map/create') {
            userACL.atCreate(values)
            dispatch(createVtParamMap(values))
            setSavedId(values.ID)
        }
        if (props.match.path === '/vtparam_map/edit/:id') {
            userACL.atUpdate(values)
            dispatch(editVtParamMap(values))
            setSavedId(values.ID)
            setTimeout(function () {
                props.history.push(`/vtparam_map/list`)
            }, 2000)
        }
    }

    return (
        <div >
            <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 4px 12px",
            }}>
                <div>
                    <div style={{ fontSize: "17px", fontWeight: 600, color: "#1f2d3d", lineHeight: 1.2 }}>
                        {params.title}
                    </div>
                    <div style={{ fontSize: "12px", color: "#8a96a8", marginTop: "2px" }}>
                        Fill in the VT param mapping details below.
                    </div>
                </div>
            </div>
            <Formik id="ledgermapForm" size="large" width={5}
                initialValues={initialValues}
                validationSchema={vtparammapSchema}
                onSubmit={(values, { resetForm }) => saveLedgermap(values, resetForm)}
                render={({ handleSubmit, resetForm, onChange, values, handleChange, setFieldValue }) => (
                    <Form as={FormikForm} size="small" className="CustomeForm" width={5} onSubmit={handleSubmit}>
                        <Form.Group widths={4} style={{ paddingBottom: "7px" }}>
                            <Field name="PARENT_GROUP" label="PARENT GROUP" component={FormikInputComponent} placeholder="PARENT GROUP" readOnly={true} className="readOnly"></Field>
                            <Field name="TRANS_TYPE" label="TRANS TYPE" component={FormikInputComponent} placeholder="TRANS TYPE" readOnly={true} className="readOnly"></Field>
                            <CommonSelect options={companyList || []} name="cid" classNameGet={"searchType"} isLabel={false} label="RealBooks: Company Name" isTxn='false' isSelection={true} 
                              getValue={function (value, setFieldValue, _values, _type, _index, fieldName) {
                                const dt = trim(values.param_display_type).toLowerCase();
                                if (dt === "grps") {
                                    // Preserve existing SEGID → SEGID mappings; only inform user if non-empty
                                    let mapObj = {};
                                    try {
                                        mapObj = JSON.parse((values.PARAM_VALUE || "{}").replace(/'/g, '"')) || {};
                                    } catch (e) { mapObj = {}; }
                                    if (Object.keys(mapObj).length > 0) {
                                        const oldCid = values.cid;
                                        const newCid = value;
                                        // The select already applied newCid; revert it so the
                                        // mapping table does NOT change until the user confirms.
                                        setFieldValue(fieldName, oldCid);
                                        dispatch(setAlertModalState({
                                            isOpen: true,
                                            type: "confirmation",
                                            size: "mini",
                                            title: "Change Company?",
                                            msg: "Showroom and True Value mapping will remove, do you want to continue?",
                                            okButton: "Yes, Change",
                                            cancelButton: "Cancel",
                                            callBack: function () {
                                                // Confirmed: apply the new company and clear mappings
                                                setFieldValue(fieldName, newCid);
                                                setFieldValue('PARAM_VALUE', '{}');
                                            }
                                        }));
                                    }
                                    // else: no mappings to lose — keep the newly selected company
                                } else {
                                    setFieldValue('PARAM_VALUE', '');
                                }
                            }} index={0} isDisable={false} defaultTxnType={""} isClearable={true} />
                        </Form.Group>
                        <Form.Group widths={4} style={{ paddingBottom: "7px" }}>
                            <Field name="PARAM_GROUP" label="PARAM GROUP" component={FormikInputComponent} placeholder="PARAM GROUP" readOnly={true} className="readOnly" />
                            <Field name="PARAM_KEY" label="PARAM KEY" component={FormikInputComponent} placeholder="PARAM KEY" readOnly={!isParamKeyEditable(values)} className={isParamKeyEditable(values) ? undefined : "readOnly"} />
                        </Form.Group>
                        <Form.Group widths={4} style={{ paddingBottom: "7px" }}>
                            {/* Conditionally render based on param_display_type */}
                            {trim(values.param_display_type) === "txt" && (<Field name="PARAM_VALUE" label="PARAM VALUE" component={FormikInputComponent} placeholder="PARAM VALUE" />)}
                            {/* Dropdown for sdd type */}
                            {trim(values.param_display_type) === "sdd" && (
                                <Field name="PARAM_VALUE">
                                    {({ field, form }) => {
                                        // Split available values and map to Yes/No labels
                                        const options = values.avlabl_key_4ui.split("#@#") || [];
                                        // If available keys are 1/0 → show "Yes"/"No"
                                        const dropdownOptions =
                                            options[0] === "1" && options[1] === "0"
                                                ? [
                                                    { key: "1", value: "1", text: "Yes" },
                                                    { key: "0", value: "0", text: "No" },
                                                ]
                                                : [
                                                    { key: "y", value: "y", text: "Yes" },
                                                    { key: "n", value: "n", text: "No" },
                                                ];
                                        return (
                                            <Form.Select
                                                label="PARAM VALUE"
                                                options={dropdownOptions}
                                                value={field.value}
                                                onChange={(e, { value }) =>
                                                    form.setFieldValue(field.name, value)
                                                }
                                                placeholder="Select Yes or No"
                                            />
                                        );
                                    }}
                                </Field>
                            )}
                            {/* Single dropdown list (sddl) - options from avlabl_key_4ui */}
                            {trim(values.param_display_type) === "sddl" && (
                                <Field name="PARAM_VALUE">
                                    {({ field, form }) => {
                                        let availableOptions = [];
                                        if (values.avlabl_key_4ui && values.avlabl_key_4ui !== "") {
                                            availableOptions = values.avlabl_key_4ui.split("#@#").filter(opt => opt && opt.trim() !== "") || [];
                                        }
                                        // Build dropdown options
                                        const dropdownOptions = availableOptions.map(opt => ({
                                            key: opt,
                                            value: opt,
                                            text: opt,
                                        }));
                                        return (
                                            <Form.Select
                                                label="PARAM VALUE"
                                                placeholder="Select Value"
                                                options={dropdownOptions}
                                                value={field.value}
                                                onChange={(e, { value }) =>
                                                    form.setFieldValue(field.name, value)
                                                }
                                            />
                                        );
                                    }}
                                </Field>
                            )}
                            {/* For multi dropdown (mdd) */}
                            {trim(values.param_display_type) === "mdd" && (
                                <Field name="PARAM_VALUE">
                                    {({ field, form }) => {
                                        let availableOptions = [];
                                        if (values.avlabl_key_4ui && values.avlabl_key_4ui !== "") {
                                            availableOptions = values.avlabl_key_4ui.split("#@#") || [];
                                        }
                                        // Build dropdown options
                                        const dropdownOptions = availableOptions.map(opt => ({
                                            key: opt,
                                            value: opt,
                                            text: opt,
                                        }));
                                        // Parse stored PARAM_VALUE safely
                                        let selectedValues = [];
                                        try {
                                            selectedValues = JSON.parse(field.value || "[]");
                                        } catch {
                                            selectedValues = [];
                                        }
                                        // On mount (first render), preselect all only if PARAM_VALUE is empty
                                        if (!selectedValues.length && availableOptions.length && !field.value) {
                                            selectedValues = availableOptions;
                                            form.setFieldValue(field.name, JSON.stringify(selectedValues));
                                        }
                                        // Handle change — now allow clearing all
                                        const handleChange = (e, { value }) => {
                                            form.setFieldValue(field.name, JSON.stringify(value || []));
                                        };
                                        return (
                                            <Form.Dropdown
                                                label="PARAM VALUE"
                                                placeholder="Select Multiple"
                                                fluid
                                                multiple
                                                selection
                                                clearable
                                                options={dropdownOptions}
                                                value={selectedValues}
                                                onChange={handleChange}
                                            />
                                        );
                                    }}
                                </Field>
                            )}
                            {/* For group type (grp) */}
                            {trim(values.param_display_type) === "grp" && (
                                <Field name="PARAM_VALUE">
                                    {({ field, form }) => {
                                        // Parse PARAM_VALUE safely
                                        let paramObject = {};
                                        try {
                                            paramObject = JSON.parse(field.value || "{}");
                                        } catch (e) {
                                            paramObject = {};
                                        }

                                        // Parse avlabl_key_4ui safely
                                        let availableKeys = [];
                                        if (values.avlabl_key_4ui && values.avlabl_key_4ui !== "") {
                                            availableKeys = values.avlabl_key_4ui.split("#@#").filter(k => k && k.trim() !== "");
                                        }

                                        // Used keys
                                        const usedKeys = Object.keys(paramObject);

                                        // Dropdown options: only unused keys
                                        const unusedOptions = availableKeys
                                            .filter(function (k) { return usedKeys.indexOf(k) === -1; })
                                            .map(function (k) {
                                                return { key: k, value: k, text: k };
                                            });

                                        // Add key to PARAM_VALUE
                                        const handleAddKey = function (key) {
                                            if (!key) return;
                                            const updated = Object.assign({}, paramObject);
                                            updated[key] = "";
                                            form.setFieldValue(field.name, JSON.stringify(updated));

                                            // remove from availableKeys and update avlabl_key_4ui
                                            const updatedAvail = availableKeys.filter(function (k) { return k !== key; });
                                            form.setFieldValue("avlabl_key_4ui", updatedAvail.join("#@#"));
                                        };

                                        // Remove key from PARAM_VALUE
                                        const handleRemoveKey = function (key) {
                                            const updated = Object.assign({}, paramObject);
                                            delete updated[key];
                                            form.setFieldValue(field.name, JSON.stringify(updated));

                                            // add back to availableKeys and update avlabl_key_4ui
                                            availableKeys.push(key);
                                            const uniq = Array.from(new Set(availableKeys));
                                            form.setFieldValue("avlabl_key_4ui", uniq.join("#@#"));
                                        };

                                        // Update value for a specific key
                                        const handleKeyValueChange = function (key, newValue) {
                                            const updated = Object.assign({}, paramObject, { [key]: newValue });
                                            form.setFieldValue(field.name, JSON.stringify(updated));
                                        };

                                        return (
                                            <div style={{ width: "50%" }}>
                                                <label style={{ fontWeight: "bold", marginBottom: "8px", display: "block" }}>
                                                    PARAM VALUE (Key–Value Table)
                                                </label>

                                                <table
                                                    style={{
                                                        width: "100%",
                                                        borderCollapse: "collapse",
                                                        border: "1px solid #ccc",
                                                        background: "#fafafa",
                                                    }}
                                                >
                                                    <thead>
                                                        <tr style={{ background: "#f0f0f0" }}>
                                                            <th style={{ border: "1px solid #ccc", padding: "6px", width: "40%" }}>Key</th>
                                                            <th style={{ border: "1px solid #ccc", padding: "6px", width: "50%" }}>Value</th>
                                                            <th style={{ border: "1px solid #ccc", padding: "6px", width: "10%" }}>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {Object.entries(paramObject).map(function ([key, val]) {
                                                            return (
                                                                <tr key={key}>
                                                                    <td style={{ border: "1px solid #ccc", padding: "6px" }}>
                                                                        <strong>{key}</strong>
                                                                    </td>
                                                                    <td style={{ border: "1px solid #ccc", padding: "6px" }}>
                                                                        <Form.Input
                                                                            placeholder={"Enter value for " + key}
                                                                            value={val}
                                                                            onChange={function (e, data) {
                                                                                handleKeyValueChange(key, data.value);
                                                                            }}
                                                                            style={{ width: "100%" }}
                                                                        />
                                                                    </td>
                                                                    <td style={{ border: "1px solid #ccc", padding: "6px", textAlign: "center" }}>
                                                                        <Button
                                                                            type="button"
                                                                            color="red"
                                                                            icon="trash"
                                                                            size="tiny"
                                                                            onClick={function () {
                                                                                handleRemoveKey(key);
                                                                            }}
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                        {Object.keys(paramObject).length === 0 && (
                                                            <tr>
                                                                <td
                                                                    colSpan="3"
                                                                    style={{
                                                                        border: "1px solid #ccc",
                                                                        padding: "8px",
                                                                        textAlign: "center",
                                                                        color: "#888",
                                                                    }}
                                                                >
                                                                    No keys in PARAM_VALUE
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>

                                                {/* Add new key dropdown */}
                                                <div style={{ marginTop: "12px", width: "40%" }}>
                                                    {unusedOptions.length > 0 ? (
                                                        <Form.Dropdown
                                                            placeholder="Select key to add"
                                                            selection
                                                            clearable
                                                            options={unusedOptions}
                                                            onChange={function (e, data) {
                                                                handleAddKey(data.value);
                                                            }}
                                                            label="Add New Key"
                                                        />
                                                    ) : (
                                                        <div style={{ fontStyle: "italic", color: "#777", marginTop: "8px" }}>
                                                            No unused keys available.
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    }}
                                </Field>
                            )}
                            {/* For multi-dropdown with segment IDs (mdds) */}
                            {trim(values.param_display_type) === "mdds" && (
                                <Field name="PARAM_VALUE">
                                    {({ field, form }) => {
                                        // ---- Step 1: Parse current PARAM_VALUE safely ----
                                        let selectedIds = [];
                                        try {
                                            selectedIds = JSON.parse(field.value.replace(/'/g, '"')) || [];
                                        } catch (e) {
                                            selectedIds = [];
                                        }

                                        // ---- Step 2: Convert segmap object to array (filtered by company via heirarchy) ----
                                        const segArray = Object.values(getAllSegmapList || {});
                                        const filteredSegArray = values.cid
                                            ? segArray.filter(function (seg) { return seg.heirarchy && seg.heirarchy.includes(':' + values.cid + ';'); })
                                            : segArray;

                                        // ---- Step 3: Extract VALID segIds from filtered segmap ----
                                        const validSegIds = filteredSegArray.map(function (s) {
                                            return String(s.segid);
                                        });

                                        // ---- Step 4: Filter selectedIds to keep ONLY valid segids ----
                                        selectedIds = selectedIds.filter(function (id) {
                                            return validSegIds.indexOf(String(id)) !== -1;
                                        });

                                        // ---- Step 5: Build dropdown options ----
                                        const options = filteredSegArray.map(function (seg) {
                                            return {
                                                key: seg.segid,
                                                value: String(seg.segid),
                                                text:
                                                    String(seg.segid) +
                                                    " - " +
                                                    (seg.name ? seg.name : "") +
                                                    " - " +
                                                    (seg.parent_group ? seg.parent_group : ""),
                                            };
                                        });

                                        // ---- Step 6: Save selectedIds back into Formik ----
                                        const handleChange = function (e, data) {
                                            const arr = data.value || [];
                                            const finalValue = JSON.stringify(arr).replace(/"/g, "'");
                                            form.setFieldValue(field.name, finalValue);
                                        };

                                        return (
                                            <div style={{ width: "100%" }}>
                                                <Form.Dropdown
                                                    label="Select Segments"
                                                    placeholder="Choose segments"
                                                    fluid
                                                    multiple
                                                    selection
                                                    clearable
                                                    options={options}
                                                    value={selectedIds}
                                                    onChange={handleChange}
                                                />

                                                <div
                                                    style={{
                                                        marginTop: "6px",
                                                        fontSize: "0.9em",
                                                        color: "#666",
                                                        fontStyle: "italic",
                                                    }}
                                                >
                                                    Saved: <strong>{JSON.stringify(selectedIds).replace(/"/g, "'")}</strong>
                                                </div>
                                            </div>
                                        );
                                    }}
                                </Field>
                            )}
                            {/* For group mapping type (grps) */}
                            {/* ------------------------ GRPS (SEGID → SEGID Mapping) ------------------------ */}
                            {trim(values.param_display_type) === "grps" && (
                                <Field name="PARAM_VALUE">
                                    {({ field, form }) => {
                                        // ------------------ parse input ------------------
                                        let mapObject = {};
                                        try {
                                            mapObject = JSON.parse(field.value.replace(/'/g, '"')) || {};
                                        } catch (e) {
                                            mapObject = {};
                                        }
                                        // ------------------ segmap → array (filtered by company via heirarchy) ------------------
                                        const segArray = Object.values(getAllSegmapList || {});
                                        const filteredSegArray = values.cid
                                            ? segArray.filter(function (seg) { return seg.heirarchy && seg.heirarchy.includes(':' + values.cid + ';'); })
                                            : segArray;
                                        // ------------------ dropdown options ------------------
                                        const segOptions = filteredSegArray.map(function (seg) {
                                            return {
                                                key: seg.segid,
                                                value: String(seg.segid),
                                                text:
                                                    String(seg.segid) +
                                                    " - " +
                                                    (seg.name ? seg.name : "") +
                                                    " - " +
                                                    (seg.parent_group ? seg.parent_group : ""),
                                            };
                                        });

                                        // ------------------ FROM options: exclude already-used FROMs ------------------
                                        const usedFromSet = new Set(Object.keys(mapObject).map(function (k) { return String(k); }));
                                        const availableFromOptions = segOptions.filter(function (o) { return !usedFromSet.has(String(o.value)); });
                                        // ------------------ trans-type-aware labels ------------------
                                        const isBcOrB = ["BC", "B"].indexOf(trim(values.TRANS_TYPE).toUpperCase()) !== -1;
                                        const fromLabel = isBcOrB ? "SHOWROOM" : "From";
                                        const toLabel = isBcOrB ? "TRUE VALUE" : "To";
                                        const sameErrorMsg = "Both values cannot be the same!";

                                        // ------------------ function: update ------------------
                                        const updateMapping = function (fromSegid, newToSegid) {
                                            const newErrors = Object.assign({}, grpsErrors);

                                            if (String(fromSegid) === String(newToSegid)) {
                                                newErrors[fromSegid] = sameErrorMsg;
                                            } else {
                                                delete newErrors[fromSegid];
                                            }

                                            setGrpsErrors(newErrors);

                                            const updated = Object.assign({}, mapObject, {
                                                [fromSegid]: newToSegid,
                                            });

                                            const save = JSON.stringify(updated).replace(/"/g, "'");
                                            form.setFieldValue(field.name, save);
                                        };

                                        // ------------------ function: delete ------------------
                                        const removeMapping = function (fromSegid) {
                                            const updated = Object.assign({}, mapObject);
                                            delete updated[fromSegid];

                                            const save = JSON.stringify(updated).replace(/"/g, "'");
                                            form.setFieldValue(field.name, save);

                                            const newErrors = Object.assign({}, grpsErrors);
                                            delete newErrors[fromSegid];
                                            setGrpsErrors(newErrors);
                                        };

                                        // ------------------ function: add mapping ------------------
                                        const addMapping = function () {
                                            if (!grpsNewFrom || !grpsNewTo) return;

                                            if (String(grpsNewFrom) === String(grpsNewTo)) {
                                                setGrpsErrors({ newRow: sameErrorMsg });
                                                return;
                                            }

                                            setGrpsErrors({});

                                            const updated = Object.assign({}, mapObject);
                                            updated[grpsNewFrom] = grpsNewTo;

                                            const save = JSON.stringify(updated).replace(/"/g, "'");
                                            form.setFieldValue(field.name, save);

                                            // reset selections after successful add
                                            setGrpsNewFrom("");
                                            setGrpsNewTo("");
                                        };

                                        // ------------------ clear newRow error on dropdown change ------------------
                                        const clearNewRowError = function () {
                                            if (grpsErrors.newRow) {
                                                const cleared = Object.assign({}, grpsErrors);
                                                delete cleared.newRow;
                                                setGrpsErrors(cleared);
                                            }
                                        };

                                        return (
                                            <div style={{ width: "50%" }}>
                                                {/* TABLE */}
                                                <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #ddd" }}>
                                                    <thead>
                                                        <tr style={{ background: "#2185d0", color: "#fff" }}>
                                                            <th style={{ padding: "8px 10px", textAlign: "left" }}>{fromLabel}</th>
                                                            <th style={{ padding: "8px 10px", textAlign: "left" }}>{toLabel}</th>
                                                            <th style={{ padding: "8px 10px", textAlign: "center", width: "80px" }}>Action</th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {Object.entries(mapObject).map(function ([fromSegid, toSegid]) {
                                                            const fromOpt = segOptions.find(function (o) { return String(o.value) === String(fromSegid); });
                                                            const fromDisplay = fromOpt ? fromOpt.text : fromSegid;
                                                            return (
                                                                <tr key={fromSegid}>
                                                                    <td style={{ border: "1px solid #ccc", padding: "6px" }}>
                                                                        <strong>{fromDisplay}</strong>
                                                                    </td>

                                                                    <td style={{ border: "1px solid #ccc", padding: "6px" }}>
                                                                        <Form.Dropdown
                                                                            fluid
                                                                            selection
                                                                            search
                                                                            options={segOptions}
                                                                            value={String(toSegid)}
                                                                            onChange={function (e, data) {
                                                                                updateMapping(fromSegid, data.value);
                                                                            }}
                                                                        />

                                                                        {grpsErrors[fromSegid] && (
                                                                            <div style={{ color: "red", fontSize: "12px" }}>
                                                                                {grpsErrors[fromSegid]}
                                                                            </div>
                                                                        )}
                                                                    </td>

                                                                    <td style={{ border: "1px solid #ccc", padding: "6px" }}>
                                                                        <Button
                                                                            type="button"
                                                                            icon="trash"
                                                                            size="tiny"
                                                                            color="red"
                                                                            onClick={function () {
                                                                                removeMapping(fromSegid);
                                                                            }}
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>

                                                {/* ADD NEW MAPPING */}
                                                <div style={{ marginTop: "20px", padding: "10px", background: "#f7f7f7" }}>
                                                    <h4>Add New Mapping</h4>

                                                    {!values.cid && (
                                                        <div style={{ color: "#b71c1c", background: "#fdecea", border: "1px solid #f5c2c0", padding: "8px 10px", borderRadius: "4px", marginBottom: "10px", fontSize: "13px" }}>
                                                            Please select <strong>Company</strong> first, then select <strong>{fromLabel}</strong> and <strong>{toLabel}</strong>.
                                                        </div>
                                                    )}

                                                    <Form.Group widths={2}>
                                                        <Form.Dropdown
                                                            fluid
                                                            selection
                                                            search
                                                            clearable
                                                            disabled={!values.cid}
                                                            placeholder={!values.cid ? "Select Company first" : ("Select " + fromLabel + " Segid")}
                                                            options={availableFromOptions}
                                                            value={grpsNewFrom}
                                                            noResultsMessage={availableFromOptions.length === 0 ? "No " + fromLabel + " Segids left — all are already mapped" : "No results found."}
                                                            onChange={function (e, data) {
                                                                setGrpsNewFrom(data.value);
                                                                clearNewRowError();
                                                            }}
                                                        />

                                                        <Form.Dropdown
                                                            fluid
                                                            selection
                                                            search
                                                            clearable
                                                            disabled={!values.cid}
                                                            placeholder={!values.cid ? "Select Company first" : ("Select " + toLabel + " Segid")}
                                                            options={segOptions}
                                                            value={grpsNewTo}
                                                            onChange={function (e, data) {
                                                                setGrpsNewTo(data.value);
                                                                clearNewRowError();
                                                            }}
                                                        />
                                                    </Form.Group>

                                                    {grpsErrors.newRow && (
                                                        <div style={{ color: "red", fontSize: "12px" }}>
                                                            {grpsErrors.newRow}
                                                        </div>
                                                    )}

                                                    <Button type="button" color="green" onClick={addMapping} disabled={!values.cid}>
                                                        Add Mapping
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    }}
                                </Field>
                            )}
                            {/* For Ledger Code Search (LCS) */}
                            {trim(values.param_display_type).toLowerCase() === "lcs" && (
                                <>
                                    <LedgerSearch
                                        name='lcs_ledger_name'
                                        label='Search Ledger Code'
                                        placeholder='Type at least 3 characters to search'
                                        dispatch={dispatch}
                                        values={values}
                                        getValue={handleLcsLedgerSelection}
                                        onBlurFunction={handleLcsLedgerOnBlur}
                                        onKeyUpFunction={null}
                                        isEditMode={isEditMode}
                                        componentType="vtparammapForm"
                                        focus={false}
                                        isTxn={true}
                                        isSelection={true}
                                        tabindex={0}
                                        className="ledgerSearch"
                                        isMandatory={false}
                                        cid={values.cid}
                                    />
                                    <Field name="PARAM_VALUE" label="PARAM VALUE" component={FormikInputComponent} placeholder="" readOnly={true}></Field>
                                </>
                            )}
                            {/* For Ledger Name Search (LNS) */}
                            {trim(values.param_display_type).toLowerCase() === "lns" && (
                                <>
                                    <LedgerSearch
                                        name='lns_ledger_search'
                                        label='Search Ledger Name'
                                        placeholder='Type at least 3 characters to search'
                                        dispatch={dispatch}
                                        values={values}
                                        getValue={handleLnsLedgerSelection}
                                        onBlurFunction={handleLnsLedgerOnBlur}
                                        onKeyUpFunction={null}
                                        isEditMode={isEditMode}
                                        componentType="vtparammapFormLNS"
                                        focus={false}
                                        isTxn={true}
                                        isSelection={true}
                                        tabindex={0}
                                        className="ledgerSearch"
                                        isMandatory={false}
                                        cid={values.cid}
                                    />
                                    <Field name="PARAM_VALUE" label="PARAM VALUE" component={FormikInputComponent} placeholder="" readOnly={true}></Field>
                                </>
                            )}
                            {/* For Ledger Code and Name Search (LCD) */}
                            {trim(values.param_display_type).toLowerCase() === "lcd" && (
                                <>
                                    <Form.Field>
                                        <LedgerSearch
                                            name='lcd_ledger_search'
                                            label='Ledger Name and Ledger Code'
                                            placeholder='Type at least 3 characters to search'
                                            dispatch={dispatch}
                                            values={values}
                                            getValue={handleLcdLedgerSelection}
                                            onBlurFunction={handleLcdLedgerOnBlur}
                                            onKeyUpFunction={null}
                                            isEditMode={isEditMode}
                                            componentType="vtparammapFormLCD"
                                            focus={false}
                                            isTxn={true}
                                            isSelection={true}
                                            tabindex={0}
                                            className="ledgerSearch"
                                            isMandatory={false}
                                            cid={values.cid}
                                            showLedgerCode={true}
                                        />
                                        {(() => {
                                            let code = "";
                                            try {
                                                const obj = JSON.parse(values.PARAM_VALUE || "{}");
                                                const keys = Object.keys(obj || {});
                                                if (keys.length > 0) code = obj[keys[0]] || "";
                                            } catch (e) { code = ""; }
                                            return code ? (
                                                <span style={{ display: "block", marginTop: "4px", fontSize: "12px", color: "#555" }}>
                                                    <strong>Ledger Code:</strong> {code}
                                                </span>
                                            ) : null;
                                        })()}
                                    </Form.Field>
                                    <Field name="PARAM_VALUE" label='PARAM VALUE {"Ledger Name":"Code"}' component={FormikInputComponent} placeholder="" readOnly={true}></Field>
                                </>
                            )}
                            {/* For Voucher Type Select (VTS) */}
                            {trim(values.param_display_type).toLowerCase() === "vts" && (
                                <Field name="PARAM_VALUE">
                                    {({ field, form }) => {
                                        var mergedOptions = [...vtOptions];
                                        if (field.value && !mergedOptions.some(function (opt) { return opt.value === field.value; })) {
                                            mergedOptions = [{ key: field.value, value: field.value, text: field.value }, ...mergedOptions];
                                        }
                                        return (
                                            <Form.Dropdown
                                                label="PARAM VALUE"
                                                placeholder="Select Voucher Type"
                                                fluid
                                                selection
                                                search
                                                loading={vtLoading}
                                                options={mergedOptions}
                                                value={field.value}
                                                onChange={function (_, data) {
                                                    form.setFieldValue(field.name, data.value);
                                                }}
                                            />
                                        );
                                    }}
                                </Field>
                            )}

                        </Form.Group>
                        <Button type="submit" size="medium" color="blue" className="CustomeBTN"> {params.submitButtonText} </Button>
                        {savedId ? <Notification id={savedId} resetForm={resetForm} notifySelector={getNotification} notifyDoneAction={setNotifyDone} type='save' /> : null}
                    </Form>
                )}
            />
        </div>
    )
}

export default VtParamMapForm;